import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError } from '../../lib/api';
import { Loader2, UserPlus } from 'lucide-react';

const ROLES = ['parent', 'coach', 'staff', 'admin'];
const roleColor = (r) => ({ admin: 'bg-purple-100 text-purple-800', staff: 'bg-blue-100 text-blue-800', coach: 'bg-teal-100 text-teal-800', parent: 'bg-gray-100 text-gray-700' }[r] || 'bg-gray-100 text-gray-700');
const emptyForm = { name: '', email: '', phone: '', password: '', role: 'staff' };

const UsersPage = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/users', { params: { role: roleFilter === 'all' ? undefined : roleFilter, limit: 100 } });
      setUsers(res.data);
    } catch (err) {
      toast({ title: 'Failed to load users', description: formatApiError(err), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [roleFilter, toast]);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (id, role) => {
    try { await api.put(`/admin/users/${id}/role`, { role }); toast({ title: `Role updated to ${role}` }); load(); }
    catch (err) { toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' }); }
  };

  const toggleActive = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/${u.is_active ? 'deactivate' : 'activate'}`);
      toast({ title: u.is_active ? 'User deactivated' : 'User activated' });
      load();
    } catch (err) { toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' }); }
  };

  const createUser = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toast({ title: 'Fill name, email and a password (6+ chars)', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/users', { name: form.name.trim(), email: form.email.trim(), phone: form.phone || null, password: form.password, role: form.role });
      toast({ title: 'User created', description: `${form.name} · ${form.role}` });
      setOpen(false); setForm(emptyForm); load();
    } catch (err) { toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff & Users</h1>
          <p className="text-gray-500 text-sm mt-1">{users.length} user{users.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-40" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {ROLES.map((r) => (<SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button onClick={() => { setForm(emptyForm); setOpen(true); }} className="bg-gradient-to-r from-blue-600 to-cyan-600" data-testid="add-user-btn">
            <UserPlus className="mr-2 h-4 w-4" /> Add User
          </Button>
        </div>
      </div>

      <Card className="shadow-md border-0 bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
          ) : users.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="users-empty">No users found.</div>
          ) : (
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                    <TableCell className="font-medium">{u.name}{u.id === user?.id && <span className="ml-2 text-xs text-gray-400">(you)</span>}</TableCell>
                    <TableCell className="text-gray-600">{u.email}</TableCell>
                    <TableCell><Badge className={roleColor(u.role)}>{u.role}</Badge></TableCell>
                    <TableCell>{u.is_active ? <Badge className="bg-green-100 text-green-800">Active</Badge> : <Badge className="bg-red-100 text-red-800">Inactive</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={u.id === user?.id}>
                          <SelectTrigger className="w-32 h-8" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{ROLES.map((r) => (<SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>))}</SelectContent>
                        </Select>
                        {u.id !== user?.id && (
                          <Button size="sm" variant="outline" className={u.is_active ? 'text-red-600' : 'text-green-600'} onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.email}`}>
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>Add Staff / User</DialogTitle>
            <DialogDescription>Create an account. The role determines their access level.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="space-y-2"><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Jane Doe" data-testid="user-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder="jane@hurupupools.com" data-testid="user-email-input" /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} placeholder="Optional" data-testid="user-phone-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Password</Label><Input type="text" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} placeholder="6+ characters" data-testid="user-password-input" /></div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => (<SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={createUser} disabled={saving} className="bg-gradient-to-r from-blue-600 to-cyan-600" data-testid="user-save-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
